/**
 * @module constants/ApplicationStateKeys
 */

export const ChronosTestModeValues = Object.freeze({
  CHRONOS_TEST_MODE_VALUE_ALL: 'all',
  CHRONOS_TEST_MODE_VALUE_AUDIO: 'audio',
  CHRONOS_TEST_MODE_VALUE_NONE: '',
  CHRONOS_TEST_MODE_VALUE_VIDEO: 'video',
} as const);

export const TunerVarStateKeys = Object.freeze({
  /* holds search redirect on startup according to server */
  SEARCH_REDIRECT_ON_STARTUP: 'SEARCH_REDIRECT_ON_STARTUP',

  /* millis of streaming inactivity allowed before messaging */
  STREAMING_INACTIVITY_TIMEOUT_MS: 'STREAMING_INACTIVITY_TIMEOUT_MS',

  /* max seconds of playback allowed to anon users */
  MAX_NUM_ANON_PLAYTIME_SECONDS: 'MAX_NUM_ANON_PLAYTIME_SECONDS',

  CS_AUTH_TOKEN: 'CS_AUTH_TOKEN',

  PASSWORD_RESET_TOKEN: 'PASSWORD_RESET_TOKEN',

  /* offer code provided by server */
  OFFER_CODE: 'OFFER_CODE',

  /* flag set by service. Do not autoplay on app launch */
  AUTOPLAY_ON_STARTUP_DISABLED: 'AUTOPLAY_ON_STARTUP_DISABLED',

  /* hostname of client host */
  APPLICATION_HOSTNAME: 'APPLICATION_HOSTNAME',
  /* port of client host */
  APPLICATION_PORT: 'APPLICATION_PORT',

  /* hostname (and port for DEV) of paid host, /billing endpoints should use this */
  PAID_HOSTNAME: 'PAID_HOSTNAME',

  /* port for stats collector */
  STATS_JSON_API_URL: 'STATS_JSON_API_URL',

  /* stats collection interval */
  SHOULD_ALWAYS_LOG_STATS: 'SHOULD_ALWAYS_LOG_STATS',
  STATS_COLLECTION_BATCH_SIZE: 'STATS_COLLECTION_BATCH_SIZE',
  STATS_COLLECTION_INTERVAL_MS: 'STATS_COLLECTION_INTERVAL_MS',

  URL_SHORTENING_SERVICE_HOST: 'URL_SHORTENING_SERVICE_HOST',
  URL_SHORTENING_SERVICE_PORT: 'URL_SHORTENING_SERVICE_PORT',

  /* force client to use flash (poss deprecated on new site) */
  FORCE_FLASH: 'FORCE_FLASH',

  /* provides the config for how to initialize the audio */
  AUDIO_PLAYER_SUPPORTED_BROWSERS: 'AUDIO_PLAYER_SUPPORTED_BROWSERS',

  /* config for supported browsers */
  SUPPORTED_BROWSERS: 'SUPPORTED_BROWSERS',

  /* should we allow users to opt-out of the new web client? */
  WEB_2016_CLIENT_OPT_OUT_ALLOWED: 'WEB_2016_CLIENT_OPT_OUT_ALLOWED',

  /* should we alert user to reload if client is out-of-date? */
  WEB_ALERT_ON_CLIENT_VERSION_MISMATCH: 'WEB_ALERT_ON_CLIENT_VERSION_MISMATCH',

  /* on paypal payment completion, show confirmation */
  REQUIRE_PAYPAL_CONFIRMATION: 'REQUIRE_PAYPAL_CONFIRMATION',

  /* country associated with client */
  COUNTRY_CODE: 'COUNTRY_CODE',

  /* is client configured to display samples */
  CAN_DISPLAY_SAMPLES: 'CAN_DISPLAY_SAMPLES',

  /* validation regexp provided by service */
  POSTAL_CODE_VALIDATION_REGEXP: 'POSTAL_CODE_VALIDATION_REGEXP',

  /* test artist message to use */
  TEST_ARTIST_MESSAGE: 'TEST_ARTIST_MESSAGE',

  /* test voice track to use */
  TEST_VOICE_TRACK: 'TEST_VOICE_TRACK',

  /* encoded artist message data */
  ON_DEMAND_ARTIST_MESSAGE_DATA: 'ON_DEMAND_ARTIST_MESSAGE_DATA',

  IS_AAMF_FLAGGING_V2: 'IS_AAMF_FLAGGING_V2',

  /* ref token supplied to FB api calls */
  FACEBOOK_REF_TOKEN: 'FACEBOOK_REF_TOKEN',

  /* the app id configured for sharing */
  FACEBOOK_SHARE_APP_ID: 'FACEBOOK_SHARE_APP_ID',

  /* permissions to pass onto FB connect */
  FACEBOOK_CONNECT_PERMISSIONS: 'FACEBOOK_CONNECT_PERMISSIONS',

  /* base url when FB sharing */
  FACEBOOK_TRACK_SHARE_BASE_URL: 'FACEBOOK_TRACK_SHARE_BASE_URL',

  /* app key for twitter oauth */
  TWITTER_SHARE_CONSUMER_KEY: 'TWITTER_SHARE_CONSUMER_KEY',

  /* app secret for twitter oauth */
  TWITTER_SHARE_CONSUMER_SECRET: 'TWITTER_SHARE_CONSUMER_SECRET',

  /* ad related keys */
  AD_ORGANIZATION: 'AD_ORGANIZATION',
  AD_PUBLISHER: 'AD_PUBLISHER',
  AD_REFRESH_INTERVAL: 'AD_REFRESH_INTERVAL',
  AD_ZONE: 'AD_ZONE',
  APS_TEST_MODE: 'APS_TEST_MODE',
  ARE_VIDEO_ADS_ENABLED: 'ARE_VIDEO_ADS_ENABLED',
  AUDIBILITY_TEST_MODE: 'AUDIBILITY_TEST_MODE',
  AUDIO_AD_TEST_MODE: 'AUDIO_AD_TEST_MODE',
  CHRONOS_TEST_MODE: 'CHRONOS_TEST_MODE',
  CREATIVE_ID: 'CREATIVE_ID',
  DFP_NETWORK_CODE: 'DFP_NETWORK_CODE',
  FORCED_AUDIO_AD_ID: 'FORCED_AUDIO_AD_ID',
  FORCED_COMPANION_AD_ID: 'FORCED_COMPANION_AD_ID',
  FORCED_DISPLAY_AD_ID: 'FORCED_DISPLAY_AD_ID',
  INTERVAL_BETWEEN_AUDIO_AND_DISPLAY_AD: 'INTERVAL_BETWEEN_AUDIO_AND_DISPLAY_AD',
  INTERVAL_BETWEEN_DISPLAY_ADS: 'INTERVAL_BETWEEN_DISPLAY_ADS',
  INTERVAL_BETWEEN_DISPLAY_AND_AUDIO_AD: 'INTERVAL_BETWEEN_DISPLAY_AND_AUDIO_AD',
  INTERVAL_BETWEEN_VIDEO_AND_DISPLAY_AD: 'INTERVAL_BETWEEN_VIDEO_AND_DISPLAY_AD',
  IS_AD_TEST_MODE: 'IS_AD_TEST_MODE',
  LINE_ID: 'LINE_ID',
  MAX_AD_INITIATED_REFRESH_DELAY: 'MAX_AD_INITIATED_REFRESH_DELAY',
  MAX_AD_INITIATED_REFRESH_DELAY_SECONDS: 'MAX_AD_INITIATED_REFRESH_DELAY_SECONDS',
  PROMOTED_STATION_ID: 'PROMOTED_STATION_ID',
  SHARE_FIRST_IMPRESSION: 'SHARE_FIRST_IMPRESSION',
  TEST_AD_SEQUENCE: 'TEST_AD_SEQUENCE',
  TEST_VIDEO_AD_ID: 'TEST_VIDEO_AD_ID',
  USE_TEST_ADS: 'USE_TEST_ADS',
  VIDEO_AD_TEST_MODE: 'VIDEO_AD_TEST_MODE',

  /* replay keys */
  FLEX_REPLAYS_COVERAGE_LOW: 'FLEX_REPLAYS_COVERAGE_LOW',
  REPLAYABLE_TRACK_HISTORY_COUNT: 'REPLAYABLE_TRACK_HISTORY_COUNT',
  VALUE_EXCHANGE_VIDEO_PROGRESS_MAX_BUFFERING_SECS:
    'VALUE_EXCHANGE_VIDEO_PROGRESS_MAX_BUFFERING_SECS',

  /* trackjs */
  TRACKJS_APP_NAME: 'TRACKJS_APP_NAME',

  /* webMonitoring */

  IS_WEB_MONITORING_ENABLED: 'IS_WEB_MONITORING_ENABLED',
  WEB_MONITORING_APP_NAME: 'WEB_MONITORING_APP_NAME',
  WEB_MONITORING_LOG_LEVEL: 'WEB_MONITORING_LOG_LEVEL',

  VIDEO_START_TIMEOUT_MS: 'VIDEO_START_TIMEOUT_MS',

  DISABLE_THIRD_PARTY_BEACONS: 'DISABLE_THIRD_PARTY_BEACONS',

  /* feature flags */
  ENABLE_ARTIST_BACKSTAGE_URQL: 'ENABLE_ARTIST_BACKSTAGE_URQL',
  ENABLE_AUDIO_PLAYER_SDK: 'ENABLE_AUDIO_PLAYER_SDK',
  ENABLE_AUTOMATIC_PLAYLIST_UNLOCK: 'ENABLE_AUTOMATIC_PLAYLIST_UNLOCK',
  ENABLE_BLACKHAWK: 'ENABLE_BLACKHAWK',
  ENABLE_BOT_DETECTION: 'ENABLE_BOT_DETECTION',
  ENABLE_COLLECTION_V7_ENDPOINTS: 'ENABLE_COLLECTION_V7_ENDPOINTS',
  ENABLE_CURATOR_DEEP_LINKING: 'ENABLE_CURATOR_DEEP_LINKING',
  ENABLE_CURATOR_EXPERIENCE: 'ENABLE_CURATOR_EXPERIENCE',
  ENABLE_DESKTOP_DEEP_LINKING: 'ENABLE_DESKTOP_DEEP_LINKING',
  ENABLE_DEVICE_UUID: 'ENABLE_DEVICE_UUID',
  ENABLE_GRAPHQL_MIGRATION: 'ENABLE_GRAPHQL_MIGRATION',
  ENABLE_CLEAR_REWARDS_ON_PA_SESSION: 'ENABLE_CLEAR_REWARDS_ON_PA_SESSION',
  ENABLE_LOG_OUT_OF_ALL_DEVICES: 'ENABLE_LOG_OUT_OF_ALL_DEVICES',
  ENABLE_MEGASTAR_MODES: 'ENABLE_MEGASTAR_MODES',
  ENABLE_NON_BINARY_GENDER: 'ENABLE_NON_BINARY_GENDER',
  ENABLE_ON_DEMAND_FAMILY_PLAN_ACCEPT_INVITE: 'ENABLE_ON_DEMAND_FAMILY_PLAN_ACCEPT_INVITE',
  ENABLE_ON_DEMAND_SPECIAL_OFFERS: 'ENABLE_ON_DEMAND_SPECIAL_OFFERS',
  ENABLE_PA_CONTENT_ID_TARGETING: 'ENABLE_PA_CONTENT_ID_TARGETING',
  ENABLE_PODCAST_TRANSCRIPTS: 'ENABLE_PODCAST_TRANSCRIPTS',
  ENABLE_PREMIUM: 'ENABLE_PREMIUM',
  ENABLE_PREMIUM_INCREASE: 'ENABLE_PREMIUM_INCREASE',
  ENABLE_RESPONSIVE_SUBSCRIPTION: 'ENABLE_RESPONSIVE_SUBSCRIPTION',
  ENABLE_STATS_TEST_LOGGER: 'ENABLE_STATS_TEST_LOGGER',
  WEB_DATADOG_ENABLED: 'WEB_DATADOG_ENABLED',
  WEB_DATADOG_CONFIG: 'WEB_DATADOG_CONFIG',
  ENABLE_USER_PROFILE_2: 'ENABLE_USER_PROFILE_2',
  ALLOW_T1_USERS_APS_STATION: 'ALLOW_T1_USERS_APS_STATION',
  PRIVACY_LAW_COMPLIANCE_ENABLED: 'PRIVACY_LAW_COMPLIANCE_ENABLED',
  WEB_CCPA_LINK_ENABLED: 'WEB_CCPA_LINK_ENABLED',
  WEB_CCPA_LINK_URL: 'WEB_CCPA_LINK_URL',

  DYNAMIC_IMAGE_SIZES: 'DYNAMIC_IMAGE_SIZES',
  DYNAMIC_IMAGE_URL: 'DYNAMIC_IMAGE_URL',
  STATIC_IMAGE_URL: 'STATIC_IMAGE_URL',

  /* my music polling interval */
  POLL_COLLECTION_INTERVAL: 'POLL_COLLECTION_INTERVAL',

  // initial pandoraId if travelling to a page where data was injected into store
  INITIAL_PANDORA_ID: 'INITIAL_PANDORA_ID',

  AMP_URLS: 'AMP_URLS',
  SHEER_ID_CLIENT_API_BASE_URL: 'SHEER_ID_CLIENT_API_BASE_URL',

  DEEP_LINKING_URL: 'DEEP_LINKING_URL',
  WEB_PANDORA_IDS_WITH_DOUBLE_SLASH_URI: 'WEB_PANDORA_IDS_WITH_DOUBLE_SLASH_URI',

  PREMIUM_ACCESS_END_TONE_URL: 'PREMIUM_ACCESS_END_TONE_URL',
  PREMIUM_ACCESS_START_TONE_URL: 'PREMIUM_ACCESS_START_TONE_URL',
  PREMIUM_ACCESS_WELCOME_MESSAGE_LIMIT: 'PREMIUM_ACCESS_WELCOME_MESSAGE_LIMIT',
  PREMIUM_ACCESS_WELCOME_MESSAGE_URL: 'PREMIUM_ACCESS_WELCOME_MESSAGE_URL',

  WEB_AD_BLOCKER_CHECK_INTERVAL: 'WEB_AD_BLOCKER_CHECK_INTERVAL',
  WEB_ANTI_AD_BLOCKER_MUSIC_TIMEOUT: 'WEB_ANTI_AD_BLOCKER_MUSIC_TIMEOUT',

  FALLBACK_ADKV_DEPRECATION_ENABLED: 'FALLBACK_ADKV_DEPRECATION_ENABLED',

  IS_UID2_ENABLED: 'IS_UID2_ENABLED',
} as const);

export const TunerVarsActions = Object.freeze({
  CLEAR_ON_DEMAND_ARTIST_MESSAGE_DATA: 'CLEAR_ON_DEMAND_ARTIST_MESSAGE_DATA',
} as const);
